import React, { useState, useEffect, useRef } from 'react';
import auth0 from 'auth0-js';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  trackCheckInPersonSuccess,
  trackCheckInPersonError,
} from '../../analytics';

import { ErrorModal } from '../../components/modals';

import config from '../../config';
import { URL } from '../../constants';

import { getEventPublic } from '../../services/api/events';
import { checkInPerson, getFaceWithFiles } from '../../services/checkInService';

import { useAttendeeStore } from '../../stores/attendee';

import { PublicEvent } from '../../types';
import { getUrl } from '../../utils';

import PageSpinner from '../General/pageSpinner';

const { featureToggles, auth0Domain, auth0ClientID } = config;

const CheckInRedirect = () => {
  const didMount = useRef(false);

  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('eventId') ?? '';

  const { attendeeEmail, attendeeName, setFace, setBearerToken } =
    useAttendeeStore();

  const [error, setError] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    async function getFace() {
      if (didMount.current || !eventId) return;

      didMount.current = true;

      const fetchedEvent = await getEventPublic(eventId);

      if (!fetchedEvent) {
        setError('Please try again later');
        return;
      }

      if (featureToggles.auth0) handleWebAuth(fetchedEvent);
      else await checkInAndNavigate(fetchedEvent);
    }

    if (attendeeEmail) getFace();
    else navigate(getUrl(URL.CHECK_IN, eventId));
  }, [eventId, attendeeEmail]);

  // auth flow
  const handleWebAuth = (fetchedEvent: PublicEvent) => {
    const webAuth = new auth0.WebAuth({
      domain: auth0Domain,
      clientID: auth0ClientID,
      responseType: 'token id_token',
      state: eventId,
    });

    webAuth.parseHash(
      { hash: window.location.hash, state: eventId, nonce: 'nonce' },
      (err, authResult) => {
        if (err) setError(err.error);

        if (authResult && authResult.accessToken)
          webAuth.client.userInfo(authResult.accessToken, async (err, user) => {
            if (err) setError(err.error);

            if (user) {
              setBearerToken(authResult.idToken ?? '');

              await checkInAndNavigate(fetchedEvent, user.email);
            }
          });
      },
    );
  };

  const checkInAndNavigate = async (event: PublicEvent, newEmail?: string) => {
    try {
      const email = (newEmail ?? attendeeEmail ?? '').toLowerCase().trim();
      const name = attendeeName ?? email.split('@')[0];

      let { face, isNew } = await checkInPerson({
        event,
        personData: { email, name },
        trackOnSuccess: trackCheckInPersonSuccess,
        trackOnError: trackCheckInPersonError,
      });

      const faceUpd = isNew
        ? { ...face, videos: [], photos: [] }
        : await getFaceWithFiles(eventId, face);

      setFace(faceUpd);

      navigate(
        `${getUrl(URL.CHECK_IN, eventId)}${isNew && !attendeeName ? '?isNew=true' : ''}`,
      );
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (!!error)
    return (
      <ErrorModal
        show
        heading="Something went wrong"
        subheading={error}
        isTryAgainBtn
        isContactUs
      />
    );

  return <PageSpinner isFullPage noTips />;
};

export default CheckInRedirect;
